"use client";

import { useRef, type ReactNode } from "react";
import { useHorizontalMenuScroller } from "@/desk/lib/use-horizontal-menu-scroller";

export type StudioHeadTab = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  badge?: number;
  iconOnly?: boolean;
};

type StudioHeadTabsProps = {
  tabs: StudioHeadTab[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  "aria-label"?: string;
  disabled?: boolean;
  /** Soft left/right fade + scrollable end spacers when the strip overflows. */
  edgeFade?: boolean;
  /**
   * Drop focus after a mouse or touch pick (keyboard picks keep it). For
   * switches that sit over a keyboard surface (workflow canvas): a clicked
   * tab kept focus, so the canvas keys (Space, Shift, Esc, arrows) turned on
   * its :focus-visible ring over the active fill.
   */
  releaseFocusOnPointer?: boolean;
};

/**
 * Shared admin/Settings head tabs — do not invent pill/segment tab chrome.
 * Overflow is a real horizontal scroller: start/end inset, scroll-padding,
 * momentum touch pan, wheel → sideways, pills never shrink/clip, and the
 * active tab is scrolled into view whenever it changes.
 */
export function StudioHeadTabs({
  tabs,
  activeId,
  onChange,
  className = "",
  "aria-label": ariaLabel = "Sections",
  edgeFade = false,
  disabled = false,
  releaseFocusOnPointer = false,
}: StudioHeadTabsProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  useHorizontalMenuScroller(stripRef, { activeKey: activeId, fade: edgeFade });
  return (
    <div
      ref={stripRef}
      className={`studio-admin-head-tabs${className ? ` ${className}` : ""}`.trim()}
      role="tablist"
      aria-label={ariaLabel}
      data-h-scroll=""
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === activeId}
          disabled={disabled}
          data-tab-id={tab.id}
          className={`studio-admin-head-tab${tab.id === activeId ? " is-active" : ""}${tab.iconOnly ? " is-icon" : ""}`}
          aria-label={tab.iconOnly && typeof tab.label === "string" ? tab.label : undefined}
          onClick={(event) => {
            // detail is 0 for Enter/Space, so keyboard users keep focus and the ring.
            if (releaseFocusOnPointer && event.detail > 0) event.currentTarget.blur();
            onChange(tab.id);
          }}
        >
          {tab.icon}
          {tab.iconOnly ? null : <span>{tab.label}</span>}
          {tab.badge ? (
            <span className="studio-admin-head-tab-badge">{tab.badge}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}
